/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// popup-manager.js — a single shared modal host, ported from Port Hippo. One
// active popup at a time; later popups QUEUE behind it, never replace it. A
// "popup" is any `{ element: HTMLElement, onMaskClick?: () => void }`. The
// `confirm` / `notify` helpers build the standard dialog skeleton, and `menu`
// shows a lightweight positioned context menu (dismissed by mask click or
// Escape) — the board right-click menu and the toolbar split-button use it.
//
// This is the only app-wide dialog/menu seam in the renderer.

import { t } from "./i18n.js";
import { el } from "./dom.js";

const state = {
  active: null, // { popup, dialogEl } currently shown, or null
  queue: [], // popups waiting behind the active one — QUEUED, never dropped
};

/**
 * Run one of a popup's callbacks, and REPORT a failure rather than dropping it.
 *
 * Every callback here is fired for effect and its return value discarded, which
 * for an `async` one means a rejection has nowhere to go: the popup closes, the
 * caller's own bookkeeping never runs, and nothing anywhere says so. That is not
 * hypothetical — it is how the close guard came to hang. `ProjectWorkspace
 * #askUnsaved` wraps its `onChoose` in a promise, and a `save()` that threw
 * instead of answering skipped the `resolve`, leaving main waiting on a reply
 * that could never arrive (there is deliberately no timeout on it, so the app
 * could not be closed again at all).
 *
 * The callback's OWN correctness is still its own business — this cannot invent
 * the answer it failed to give. What it does is make the failure loud, so the
 * next one is a console line instead of a wedged app.
 */
function fire(fn, ...args) {
  if (typeof fn !== "function") return;
  try {
    const out = fn(...args);
    if (out && typeof out.then === "function") {
      out.then(null, (err) => console.error("[popup] callback failed:", err));
    }
  } catch (err) {
    console.error("[popup] callback failed:", err);
  }
}

/** A close "×" glyph for a dialog()'s header button. */
const CLOSE_SVG =
  '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" ' +
  'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" ' +
  'aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>';

/** The smaller "×" a removable menu row carries (an Open Recent entry). */
const REMOVE_SVG =
  '<svg viewBox="0 0 12 12" width="10" height="10" fill="none" ' +
  'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" ' +
  'aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6"/></svg>';

/** The tick a CHECKED menu item carries in its icon slot. */
const CHECK_SVG =
  '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" ' +
  'stroke="currentColor" stroke-width="1.8" stroke-linecap="round" ' +
  'stroke-linejoin="round" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7"/></svg>';

/** Keep a positioned card (menu or submenu) fully on-screen, with a margin. */
function placeCard(node, x, y) {
  const rect = node.getBoundingClientRect();
  const pad = 8;
  const left = Math.max(pad, Math.min(x, window.innerWidth - rect.width - pad));
  const top = Math.max(
    pad,
    Math.min(y, window.innerHeight - rect.height - pad),
  );
  node.style.left = `${left}px`;
  node.style.top = `${top}px`;
}

/** The disabled placeholder a submenu shows when it has nothing to list. */
function emptyMenuItem(label) {
  return el(
    "button",
    {
      class: "popup-menu-item popup-menu-item--empty",
      type: "button",
      role: "menuitem",
      disabled: true,
    },
    [el("span", { class: "popup-menu-label", text: label })],
  );
}

/**
 * One menu row. Plain items are a single `<button.popup-menu-item>` (so a
 * menu with no icons/accelerators/submenus renders exactly as it always
 * has); a REMOVABLE item is that button wrapped in a row beside its own ×,
 * since a button can't nest another one.
 */
function menuItemNode(item, ctx) {
  if (item.separator) {
    return el("div", { class: "popup-menu-separator", role: "separator" });
  }

  const checkable = typeof item.checked === "boolean";
  const icon =
    ctx.hasIcons &&
    el("span", { class: "popup-menu-icon", "aria-hidden": "true" });
  if (icon && item.icon) icon.innerHTML = item.icon;
  else if (icon && item.checked === true) icon.innerHTML = CHECK_SVG;

  const swatch =
    item.swatch &&
    el("span", { class: "popup-menu-swatch", "aria-hidden": "true" });
  if (swatch) swatch.style.background = item.swatch;

  const button = el(
    "button",
    {
      class: `popup-menu-item${item.danger ? " popup-menu-item--danger" : ""}`,
      type: "button",
      role: checkable ? "menuitemcheckbox" : "menuitem",
      "aria-checked": checkable ? String(item.checked) : null,
      title: item.title,
      disabled: Boolean(item.disabled),
      "aria-haspopup": item.submenu ? "menu" : null,
      onClick: () => {
        if (item.submenu) {
          ctx.toggleFlyout(button, item);
          return;
        }
        ctx.close();
        fire(item.onSelect);
      },
    },
    [
      icon,
      swatch,
      el("span", { class: "popup-menu-label", text: item.label }),
      item.accelerator &&
        el("span", { class: "popup-menu-accel", text: item.accelerator }),
      item.submenu &&
        el("span", {
          class: "popup-menu-chevron",
          "aria-hidden": "true",
          text: "›",
        }),
    ],
  );
  if (!item.disabled) {
    button.addEventListener("pointerenter", () => {
      if (item.submenu) ctx.openFlyout(button, item);
      else ctx.leaveFlyout(button);
    });
  }
  if (!item.onRemove) return button;

  const removeLabel =
    item.removeLabel ?? t("popup.removeNamed", { label: item.label });
  const removeBtn = el("button", {
    class: "popup-menu-remove",
    type: "button",
    title: removeLabel,
    "aria-label": removeLabel,
    onClick: (event) => {
      // Dropping an entry is not a selection: the menu stays open so several
      // can go in one visit, and the card falls back to its empty placeholder
      // once the last row is gone.
      event.stopPropagation();
      const row = event.currentTarget.closest(".popup-menu-row");
      const card = row?.parentElement;
      row?.remove();
      fire(item.onRemove);
      if (card && !card.querySelector(".popup-menu-item")) {
        const label = card.dataset.emptyLabel;
        if (label) card.append(emptyMenuItem(label));
      }
    },
  });
  removeBtn.innerHTML = REMOVE_SVG;
  return el("div", { class: "popup-menu-row" }, [button, removeBtn]);
}

/**
 * A menu card: the root menu, or a submenu flyout. `emptyLabel` is both the
 * placeholder for an empty list and — stored on the card — what a row removal
 * falls back to when it empties the card.
 */
function buildCard(items, emptyLabel, ctx) {
  const hasIcons = items.some(
    (item) => item.icon || typeof item.checked === "boolean",
  );
  const cardCtx = { ...ctx, hasIcons };
  const card = el(
    "div",
    { class: "popup-menu", role: "menu" },
    items.length
      ? items.map((item) => menuItemNode(item, cardCtx))
      : emptyLabel
        ? [emptyMenuItem(emptyLabel)]
        : [],
  );
  if (emptyLabel) card.dataset.emptyLabel = emptyLabel;
  return card;
}

/** Focus `opener` again — if it is still on the page and can take it. */
function returnFocus(opener) {
  if (!opener || !opener.isConnected || opener === document.body) return;
  if (typeof opener.focus !== "function") return;
  opener.focus({ preventScroll: true });
}

function dismiss(popup) {
  (popup.onMaskClick || PopupManager.close)();
}

// Mount a popup as a native modal <dialog>. showModal() puts it in the browser
// TOP LAYER, so a popup raised while another dialog is open stacks ABOVE it.
// Escape → native `cancel`; a click landing on the dialog itself (not its
// content) is a backdrop click — but only when the PRESS landed there too. A
// click's target is the nearest common ancestor of its press and release, so a
// text selection begun in a field and released out over the backdrop arrives
// as a click on the dialog itself; dismissing on it would throw the dialog
// away along with the text just selected to copy.
//
// The control that had focus when the popup mounted is remembered as its
// OPENER, and close() hands focus back to it: left to the browser, focus
// falls to whatever <dialog> the popup closed over — a transparent frame with
// no ring (`.popup-dialog { outline: none }`) — or to <body>, so a keyboard
// user is left nowhere visible.
function mount(popup) {
  const opener = document.activeElement;
  const dialogEl = el(
    "dialog",
    {
      class: `popup-dialog${popup.variant ? ` popup-dialog--${popup.variant}` : ""}`,
    },
    [popup.element],
  );
  dialogEl.addEventListener("cancel", (event) => {
    event.preventDefault();
    dismiss(popup);
  });
  let pressedOutside = false;
  dialogEl.addEventListener("pointerdown", (event) => {
    pressedOutside = event.target === dialogEl;
  });
  dialogEl.addEventListener("click", (event) => {
    const outside = pressedOutside && event.target === dialogEl;
    pressedOutside = false;
    if (outside) dismiss(popup);
  });
  document.body.appendChild(dialogEl);
  state.active = { popup, dialogEl, opener };
  dialogEl.showModal();
  // A POSITIONED popup (menu / popover) is placed only NOW: a card has to be
  // mounted and shown before it can be measured, and a popup QUEUED behind
  // another mounts long after its coordinates were named — placing it when it
  // was asked for would clamp a node that is still unmounted and zero-size.
  if (popup.place) placeCard(popup.element, popup.place.x, popup.place.y);

  const focusTarget =
    popup.element.querySelector("[data-autofocus]") ||
    popup.element.querySelector("button:enabled");
  focusTarget?.focus();
}

export const PopupManager = {
  /** True while any popup (dialog or menu) is mounted and showing. */
  isOpen() {
    return state.active != null;
  },

  /**
   * Mount a popup. If one is already open the new popup is QUEUED (shown when
   * the current one closes) rather than replacing it. Focuses the first
   * `[data-autofocus]` control, or the first enabled button. `place` is what
   * makes a popup POSITIONED rather than centred: its card is clamped to those
   * viewport coordinates when it mounts (see mount()).
   * @param {{ element: HTMLElement, onMaskClick?: () => void,
   *   onClose?: () => void, variant?: string,
   *   place?: {x: number, y: number} }} popup
   */
  open(popup) {
    if (!popup || !popup.element) return;
    // Defensive: a detached active dialog means the previous popup is gone
    // (e.g. the DOM was reset under tests) — treat the host as idle rather
    // than queueing behind a ghost.
    if (
      state.active &&
      (!state.active.dialogEl.isConnected ||
        state.active.dialogEl.ownerDocument !== document)
    ) {
      state.active = null;
      state.queue = [];
    }
    if (state.active) {
      state.queue.push(popup);
      return;
    }
    mount(popup);
  },

  /** Close the active popup, then show the next queued one. Safe to call idle. */
  close() {
    const active = state.active;
    state.active = null;
    if (active) {
      try {
        active.dialogEl.close();
      } catch {
        // already closed
      }
      active.dialogEl.remove();
      // Before onClose, so a callback that moves focus itself still wins.
      returnFocus(active.opener);
      // Scoped notice: reset the CLOSED popup's own open-guard, whichever way it
      // was dismissed (button, mask, or Escape). This must be per-popup, not a
      // broadcast — a stateful dialog QUEUED behind another popup would reset
      // its guard when the OTHER popup closed (just before it mounts), leaving
      // it visible-but-unguarded and stackable.
      fire(active.popup.onClose);
      // The un-scoped broadcast is retained for any incidental listener, but the
      // guard reset above no longer depends on it.
      window.dispatchEvent(new CustomEvent("chiphippo:popup-closed"));
    }
    const next = state.queue.shift();
    if (next) mount(next);
  },

  /**
   * A lightweight context/dropdown menu at screen coordinates (clamped into
   * the viewport). An item is
   * `{ label, disabled?, danger?, swatch?, icon?, checked?, accelerator?,
   *    title?, submenu?, emptyLabel?, onSelect?, onRemove?, removeLabel? }`,
   * or `{ separator: true }` for a divider rule:
   *   - `swatch` — a CSS color rendering a dot before the label (the wire/bus
   *     color pickers).
   *   - `icon` — an SVG source string drawn in a leading 16 px slot; when ANY
   *     item in a card has one, the others get an empty slot so every label
   *     still lines up.
   *   - `checked` — a boolean makes the item a CHECKBOX item
   *     (`menuitemcheckbox`), its tick in the icon slot while true (a custom
   *     chip's breakpoint toggles).
   *   - `accelerator` — a right-aligned shortcut hint ("⌘S").
   *   - `submenu` — nested items shown in a flyout card beside this one, which
   *     opens on hover or click; the item's own `emptyLabel` is the disabled
   *     placeholder for an empty (or emptied) flyout.
   *   - `onRemove` — renders a trailing × that DROPS THE ROW in place and
   *     leaves the menu open (removing an Open Recent entry), rather than
   *     selecting anything.
   * A selection closes the whole menu first, then runs onSelect. Mask click /
   * Escape dismiss with no selection. The card's own `emptyLabel` is the
   * disabled placeholder when `items` is empty (or is emptied by an `onRemove`)
   * — without one, an empty list opens an empty card.
   * @param {{ x: number, y: number, items: Array<object>,
   *   emptyLabel?: string }} opts
   */
  menu({ x = 0, y = 0, items = [], emptyLabel = null } = {}) {
    // At most one flyout is open at a time; it lives beside the root card in
    // the same dialog (a fixed-position sibling), so it is never clipped by
    // the card's rounded box and dies with the popup.
    let flyout = null; // { anchor, card }

    const closeFlyout = () => {
      if (!flyout) return;
      flyout.card.remove();
      flyout.anchor.classList.remove("popup-menu-item--open");
      flyout = null;
    };

    const openFlyout = (anchor, item) => {
      if (flyout?.anchor === anchor) return;
      closeFlyout();
      const card = buildCard(item.submenu ?? [], item.emptyLabel, ctx);
      (anchor.closest("dialog") ?? document.body).append(card);
      anchor.classList.add("popup-menu-item--open");
      flyout = { anchor, card };
      const rect = anchor.getBoundingClientRect();
      placeCard(card, rect.right + 2, rect.top - 4);
    };

    const ctx = {
      close: () => this.close(),
      openFlyout,
      // Hovering a plain item closes the open flyout — unless that item IS in
      // the flyout (moving onto its own entries must not shut it).
      leaveFlyout: (button) => {
        if (flyout && !flyout.card.contains(button)) closeFlyout();
      },
      toggleFlyout: (anchor, item) => {
        if (flyout?.anchor === anchor) closeFlyout();
        else openFlyout(anchor, item);
      },
    };

    const menuEl = buildCard(items, emptyLabel, ctx);
    this.open({ element: menuEl, variant: "menu", place: { x, y } });
  },

  /**
   * A small POPOVER at screen coordinates: `menu`'s positioned, non-dimming
   * host with the caller's OWN DOM in the card instead of an items array (the
   * Wire button's color picker). Clamped into the viewport exactly as a menu
   * is, and dismissed the same two ways — a click outside it, or Escape.
   *
   * The card is a plain SURFACE and adds no role of its own: whatever goes in
   * brings its own semantics (the color picker is a `role="radiogroup"`), and
   * wrapping that in an unnamed dialog layer would only bury it. For the same
   * reason a popover never closes itself when something inside it is used —
   * that is the content's call, so a picker calls `PopupManager.close()` in
   * its own handler.
   * @param {{ x: number, y: number, element: HTMLElement,
   *   onClose?: () => void }} opts
   */
  popover({ x = 0, y = 0, element, onClose } = {}) {
    if (!element) return;
    const card = el("div", { class: "popup-popover" }, [element]);
    this.open({ element: card, variant: "popover", place: { x, y }, onClose });
  },

  /**
   * The standard "header (title + × close button) over a scrollable body"
   * modal shape — About/Settings/Properties/Keyboard-Shortcuts all build this
   * by hand before; this is that ceremony in one place. The caller supplies
   * only its own body content and its `static #open` guard's reset.
   * @param {object} opts
   * @param {string} opts.title - header title text; also the dialog's default
   *   `aria-label`.
   * @param {string} [opts.ariaLabel] - overrides the dialog's `aria-label`
   *   (defaults to `title`).
   * @param {string} [opts.closeAriaLabel] - the close button's `aria-label`
   *   (e.g. "Close settings"); defaults to `Close ${title}`.
   * @param {string} [opts.className] - an extra class on the popup root
   *   (alongside "popup"), e.g. "settings-popup".
   * @param {string} [opts.bodyClass] - an extra class on the `.popup-body`
   *   wrapper, e.g. "settings-popup-body".
   * @param {Node|Array<Node>} opts.body - the dialog's own content.
   * @param {Array<Node>} [opts.headerActions] - buttons for the header's top
   *   right, LEFT of the close (×) and grouped with it (the Serial I/O cards'
   *   protocol-reference button). Absent, the header is title + × as ever.
   * @param {() => void} [opts.onClose] - fires when THIS popup closes (not
   *   when a popup it was queued behind closes) — reset the caller's guard.
   */
  dialog({
    title,
    ariaLabel,
    closeAriaLabel,
    className,
    bodyClass,
    body,
    headerActions,
    onClose,
  } = {}) {
    const closeBtn = el("button", {
      class: "popup-close",
      type: "button",
      title: t("common.close"),
      "aria-label": closeAriaLabel ?? t("popup.closeNamed", { title }),
      onClick: () => this.close(),
      "data-autofocus": true,
    });
    closeBtn.innerHTML = CLOSE_SVG;
    // The header's own buttons sit in ONE group with the ×, a tight row of
    // icons rather than spread by `.popup-header`'s title gap.
    const trailing = headerActions?.length
      ? el("div", { class: "popup-header-actions" }, [
          ...headerActions,
          closeBtn,
        ])
      : closeBtn;

    const element = el(
      "div",
      {
        class: `popup${className ? ` ${className}` : ""}`,
        role: "dialog",
        "aria-modal": "true",
        "aria-label": ariaLabel ?? title,
      },
      [
        el("div", { class: "popup-header" }, [
          el("span", { class: "popup-title", text: title }),
          trailing,
        ]),
        el(
          "div",
          { class: `popup-body${bodyClass ? ` ${bodyClass}` : ""}` },
          body,
        ),
      ],
    );

    this.open({ element, onMaskClick: () => this.close(), onClose });
  },

  /**
   * A two-button confirmation dialog. `onConfirm` / `onCancel` fire after the
   * dialog closes. `confirmClass` styles the confirm button (e.g.
   * "btn--danger").
   * @param {object} opts
   */
  confirm({
    title,
    message,
    note,
    confirmLabel = t("common.confirm"),
    cancelLabel = t("common.cancel"),
    confirmClass = "btn--primary",
    onConfirm,
    onCancel,
  } = {}) {
    const done = (fn) => () => {
      this.close();
      fire(fn);
    };

    // A destructive confirm (btn--danger) autofocuses Cancel, not Confirm, so a
    // reflexive Enter right after the dialog opens never fires the dangerous
    // action. Only a non-destructive confirm autofocuses Confirm.
    const isDanger = confirmClass.includes("danger");

    const element = el(
      "div",
      {
        class: "popup popup-confirm",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": title || message || "",
      },
      [
        title &&
          el("div", { class: "popup-header" }, [
            el("span", { class: "popup-title", text: title }),
          ]),
        el(
          "div",
          { class: "popup-body" },
          [
            message && el("p", { class: "popup-message", text: message }),
            note && el("p", { class: "popup-note", text: note }),
          ].filter(Boolean),
        ),
        el("div", { class: "popup-footer" }, [
          el("button", {
            class: "btn popup-btn btn--secondary",
            type: "button",
            text: cancelLabel,
            onClick: done(onCancel),
            "data-autofocus": isDanger,
          }),
          el("button", {
            class: `btn popup-btn ${confirmClass}`,
            type: "button",
            text: confirmLabel,
            onClick: done(onConfirm),
            "data-autofocus": !isDanger,
          }),
        ]),
      ].filter(Boolean),
    );

    this.open({ element, onMaskClick: done(onCancel) });
  },

  /**
   * A dialog with MORE than two ways out — the "Cancel / Save / Discard"
   * shape a close-with-unsaved-changes has to offer (Feature 240's tab
   * delete). `confirm` can't express it: there the only question is yes or no,
   * and here "no" splits into two different answers.
   *
   * Choices render left-to-right after Cancel, each `{ label, value, class }`;
   * `onChoose(value)` fires after the dialog closes, and a Cancel / mask click
   * / Escape calls it with `null` — one exit path, so a caller can never miss
   * a dismissal. Cancel autofocuses: the safe answer is always the default.
   *
   * @param {object} opts
   * @param {string} [opts.title]
   * @param {string} [opts.message]
   * @param {Array<{label: string, value: *, class?: string}>} opts.choices
   * @param {string} [opts.cancelLabel]
   * @param {(value: *) => void} [opts.onChoose]
   */
  choose({
    title,
    message,
    note,
    choices = [],
    cancelLabel = t("common.cancel"),
    onChoose,
  } = {}) {
    const done = (value) => () => {
      this.close();
      fire(onChoose, value);
    };
    const element = el(
      "div",
      {
        class: "popup popup-confirm",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": title || message || "",
      },
      [
        title &&
          el("div", { class: "popup-header" }, [
            el("span", { class: "popup-title", text: title }),
          ]),
        el(
          "div",
          { class: "popup-body" },
          [
            message && el("p", { class: "popup-message", text: message }),
            note && el("p", { class: "popup-note", text: note }),
          ].filter(Boolean),
        ),
        el("div", { class: "popup-footer" }, [
          el("button", {
            class: "btn popup-btn btn--secondary",
            type: "button",
            text: cancelLabel,
            onClick: done(null),
            "data-autofocus": true,
          }),
          ...choices.map((choice) =>
            el("button", {
              class: `btn popup-btn ${choice.class ?? "btn--primary"}`,
              type: "button",
              text: choice.label,
              onClick: done(choice.value),
            }),
          ),
        ]),
      ].filter(Boolean),
    );
    this.open({ element, onMaskClick: done(null) });
  },

  /**
   * A one-field text-input dialog. `quickPicks` render as buttons that fill
   * (and immediately submit) the field. `onConfirm(value)` fires with the
   * trimmed text after the dialog closes; an empty value is passed through, so
   * the caller decides whether to ignore it. Enter submits, Escape cancels.
   * @param {object} opts
   */
  prompt({
    title,
    message,
    label = "",
    value = "",
    placeholder = "",
    quickPicks = [],
    confirmLabel = t("common.ok"),
    cancelLabel = t("common.cancel"),
    onConfirm,
    onCancel,
  } = {}) {
    const input = el("input", {
      class: "popup-input",
      type: "text",
      value,
      placeholder,
      "data-autofocus": true,
    });

    const submit = () => {
      const text = input.value.trim();
      this.close();
      fire(onConfirm, text);
    };
    const cancel = () => {
      this.close();
      fire(onCancel);
    };

    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        submit();
      }
    });

    const picks =
      quickPicks.length > 0 &&
      el(
        "div",
        { class: "popup-quickpicks" },
        quickPicks.map((name) =>
          el("button", {
            class: "btn popup-btn btn--secondary popup-quickpick",
            type: "button",
            text: name,
            onClick: () => {
              input.value = name;
              submit();
            },
          }),
        ),
      );

    const element = el(
      "div",
      {
        class: "popup popup-prompt",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": title || label || t("popup.enterValue"),
      },
      [
        title &&
          el("div", { class: "popup-header" }, [
            el("span", { class: "popup-title", text: title }),
          ]),
        el(
          "div",
          { class: "popup-body" },
          [
            message && el("p", { class: "popup-message", text: message }),
            label && el("label", { class: "popup-label", text: label }),
            input,
            picks,
          ].filter(Boolean),
        ),
        el("div", { class: "popup-footer" }, [
          el("button", {
            class: "btn popup-btn btn--secondary",
            type: "button",
            text: cancelLabel,
            onClick: cancel,
          }),
          el("button", {
            class: "btn popup-btn btn--primary",
            type: "button",
            text: confirmLabel,
            onClick: submit,
          }),
        ]),
      ].filter(Boolean),
    );

    this.open({ element, onMaskClick: cancel });
  },

  /**
   * A single-button acknowledgement dialog (no cancel).
   * @param {object} opts
   */
  notify({
    title,
    message,
    okLabel = t("common.ok"),
    okClass = "btn--primary",
    onClose,
  } = {}) {
    const element = el(
      "div",
      {
        class: "popup popup-notify",
        role: "alertdialog",
        "aria-modal": "true",
        "aria-label": title || message || "",
      },
      [
        title &&
          el("div", { class: "popup-header" }, [
            el("span", { class: "popup-title", text: title }),
          ]),
        el("div", { class: "popup-body" }, [
          message && el("p", { class: "popup-message", text: message }),
        ]),
        el("div", { class: "popup-footer" }, [
          el("button", {
            class: `btn popup-btn ${okClass}`,
            type: "button",
            text: okLabel,
            onClick: () => {
              this.close();
              fire(onClose);
            },
            "data-autofocus": true,
          }),
        ]),
      ].filter(Boolean),
    );

    this.open({
      element,
      onMaskClick: () => {
        this.close();
        fire(onClose);
      },
    });
  },
};
